import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GenericLessonLayout, GITPanel, ThreeVoices, RulesOut, DiscussionQuestions, DigDeeper, BookReadingHook } from './LessonComponents';
import { useTheme } from '../../context/ThemeContext';
import BetaFeedbackPanel from './BetaFeedbackPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DynamicLesson() {
  const { moduleKey } = useParams();
  const { token } = useAuth();
  const theme = useTheme();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchLesson() {
      setLoading(true);
      setError(null);
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/lessons/${moduleKey}`, {
          headers,
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Lesson not found');
        const data = await res.json();
        setLesson(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (moduleKey) fetchLesson();
  }, [moduleKey, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgPrimary }}>
        <p style={{ color: theme.textSecondary }} data-testid="lesson-loading">Loading lesson...</p>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgPrimary }}>
        <div className="text-center" data-testid="lesson-error">
          <p className="text-lg mb-2" style={{ color: theme.labelColor }}>Lesson Not Available</p>
          <p className="text-sm" style={{ color: theme.textSecondary }}>This lesson content is not yet available or you don't have access.</p>
        </div>
      </div>
    );
  }

  const git = lesson.git_panel;
  const voices = lesson.three_voices;
  const hook = lesson.book_hook;

  return (
    <GenericLessonLayout
      lessonType={lesson.lesson_type || 'interior'}
      lessonNum={lesson.lesson_num || 1}
      title={lesson.title || 'Untitled Lesson'}
      moduleKey={lesson.module_key}
      nextRoute={lesson.next_route || '/'}
      nextLabel={lesson.next_label || 'Continue'}
      accentColor={lesson.accent_color || theme.accentPrimary}
      icon={lesson.icon || ''}
    >
      {/* Opening paragraphs */}
      {(lesson.opening_paragraphs?.length > 0 || lesson.opening_scripture_ref) && (
        <div className="mb-12" data-testid="opening-teaching">
          {lesson.opening_scripture_ref && (
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.textMuted }}>
              {lesson.opening_scripture_ref}
            </p>
          )}
          {lesson.opening_paragraphs?.map((p, i) => (
            <p key={i} className="text-base leading-relaxed mb-4" style={{ color: theme.textPrimary }}>{p}</p>
          ))}
        </div>
      )}

      {/* Grade School Summary */}
      {lesson.grade_school_summary && (
        <div className="mb-12 px-5 py-5 rounded-xl border" data-testid="grade-school-summary"
          style={{
            background: 'rgba(139,92,246,0.04)',
            borderColor: 'rgba(139,92,246,0.15)',
          }}>
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="rgba(139,92,246,0.7)" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
            </svg>
            <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: 'rgba(139,92,246,0.7)' }}>
              Simple Summary
            </span>
          </div>
          <p className="text-sm leading-relaxed italic" style={{ color: theme.textSecondary }}>
            {lesson.grade_school_summary}
          </p>
        </div>
      )}

      {/* GIT Panel */}
      {git && git.verse && (
        <GITPanel
          verse={git.verse}
          verseRef={git.verse_ref}
          anchorPhrase={git.anchor_phrase}
          answers={git.answers || []}
        />
      )}

      {/* Three Voices */}
      {voices && (voices.teacher || voices.professor || voices.pastoral) && (
        <ThreeVoices
          teacher={voices.teacher}
          professor={voices.professor}
          pastoral={voices.pastoral}
        />
      )}

      {/* Rules Out */}
      {lesson.rules_out && <RulesOut text={lesson.rules_out} />}

      {/* Book Reading Hook */}
      {hook && hook.chapter_num && (
        <BookReadingHook
          chapterNum={hook.chapter_num}
          chapterTitle={hook.chapter_title}
          previewText={hook.preview_text}
          appendixChapterNum={hook.appendix_chapter_num}
          appendixTitle={hook.appendix_title}
        />
      )}

      {/* Discussion Questions */}
      {lesson.discussion_questions?.length > 0 && (
        <DiscussionQuestions questions={lesson.discussion_questions} />
      )}

      {/* Dig Deeper */}
      {lesson.dig_deeper?.length > 0 && (
        <DigDeeper links={lesson.dig_deeper.map(l => ({
          code: l.code,
          title: l.title,
          bookId: l.book_id,
          chapterNum: l.chapter_num,
        }))} />
      )}

      {/* Beta Tester Feedback Panel */}
      <BetaFeedbackPanel moduleKey={lesson.module_key} />
    </GenericLessonLayout>
  );
}
